/**
 * The ten MVP kingdoms (`docs/rpg.md` §50, `docs/rpg-implementation-plan.md`
 * §0.9).
 *
 * A kingdom is a named grouping of concepts that already exist in
 * `content/src/concepts/` — the fantasy is a presentation of the graph, never a
 * second curriculum. Regions echo the mathematics they hold ("Equation Forest"
 * is where the linear equations are), and the grouping follows the `UNITS` in
 * `courses.ts` wherever a unit already tells the truth about what belongs
 * together.
 *
 * Two rules hold this file together, both enforced by `validateWorld`:
 *
 *   - every concept with status `available` sits in exactly one region, so
 *     finishing any lesson on the platform moves some kingdom's bar;
 *   - an unlock requirement may only name concepts from kingdoms of strictly
 *     lower `order`, so the chain of seals can never form a loop.
 *
 * No `planned` concept is listed here yet. The status math tolerates them — a
 * region may name them to show where the mathematics leads, and they simply
 * never count as known — but a bar that can never fill is a poor first
 * impression, and the content pass in PR 13 is where the unreachable parts of
 * the map get drawn.
 *
 * `bossId` names a boss in `bosses.ts`, and `validateBosses` insists the two
 * agree in both directions: a kingdom pointing at a boss nobody defined is a
 * seal with no key, because a kingdom with a boss is only ever `cleared` by
 * beating it. All ten have one, which is where the MVP map was always going —
 * but a kingdom without a boss is a supported state rather than an unfinished
 * one, and simply finishes itself when there is nothing left in it to learn.
 * Starter Village's is a gate trial rather than a monster — a village has no
 * dragon, but it does have a road out and somebody keeping the ledger at the
 * end of it.
 *
 * `order` is a position on the map and the rung the chain of seals is checked
 * against — never a claim about difficulty, and never a prerequisite in
 * disguise. The Linear Algebra Kingdom sits at 5, below Function and Calculus,
 * because it is reached from vectors and systems and depends on neither.
 */

import type { Kingdom, Region, UnlockRequirement } from '@yuan-math/world';
import type { StrandId } from '@yuan-math/curriculum';
import { STRANDS } from '../levels.ts';

/* -------------------------------------------------------------------------- */
/* Starter Village                                                            */
/* -------------------------------------------------------------------------- */

const COUNTING_MEADOW: Region = {
  id: 'region-counting-meadow',
  name: 'Counting Meadow',
  emoji: '🌾',
  flavor: 'Where every traveller starts: counting what is in front of you, and spotting what comes next.',
  conceptIds: ['counting', 'number-patterns'],
};

const SUM_COTTAGE: Region = {
  id: 'region-sum-cottage',
  name: 'Sum Cottage',
  emoji: '➕',
  flavor: 'A cottage of carrying and borrowing, where adding and subtracting turn out to be the same door.',
  conceptIds: ['multi-digit-addition', 'multi-digit-subtraction', 'fact-families'],
};

const TIMES_TABLE_GROVE: Region = {
  id: 'region-times-table-grove',
  name: 'Times Table Grove',
  emoji: '✖️',
  flavor: 'Rows of trees planted in rectangles — the grove where multiplication stops being repeated addition.',
  conceptIds: ['multiplication-facts', 'multiplication-properties', 'multiply-by-tens'],
};

const SHARING_BARN: Region = {
  id: 'region-sharing-barn',
  name: 'Sharing Barn',
  emoji: '➗',
  flavor: 'Split the harvest fairly and see what is left over. Division, and the remainder nobody can share.',
  conceptIds: ['division-meaning', 'division-remainders', 'multiplicative-comparison'],
};

const TWO_STEP_TRAIL: Region = {
  id: 'region-two-step-trail',
  name: 'Two-Step Trail',
  emoji: '🥾',
  flavor: 'The path out of the village, where one calculation is never enough to finish the journey.',
  conceptIds: ['two-step-problems', 'multiply-multi-digit', 'long-division'],
};

/* -------------------------------------------------------------------------- */
/* Number Kingdom                                                             */
/* -------------------------------------------------------------------------- */

const PLACE_VALUE_PLAINS: Region = {
  id: 'region-place-value-plains',
  name: 'Place Value Plains',
  emoji: '🔢',
  flavor: 'Flat country marked out in ones, tens and hundreds, where a digit means what its column says it means.',
  conceptIds: ['place-value', 'rounding', 'powers-of-ten', 'order-of-operations'],
};

const DECIMAL_DOCKS: Region = {
  id: 'region-decimal-docks',
  name: 'Decimal Docks',
  emoji: '⚓',
  flavor: 'Harbour of the tenths and hundredths, where the point holds the line between whole and part.',
  conceptIds: [
    'decimal-place-value',
    'compare-decimals',
    'add-decimals',
    'fractions-to-decimals',
    'round-decimals',
    'multiply-decimals',
    'divide-decimals',
    'decimal-operations',
  ],
};

const INTEGER_CAVERNS: Region = {
  id: 'region-integer-caverns',
  name: 'Integer Caverns',
  emoji: '❄️',
  flavor: 'Below sea level the numbers keep going. Cold caves of negatives, opposites and distance from zero.',
  conceptIds: ['integers', 'absolute-value-opposites', 'rational-numbers', 'real-numbers'],
};

const PRIME_FOUNDRY: Region = {
  id: 'region-prime-foundry',
  name: 'Prime Foundry',
  emoji: '⚒️',
  flavor: 'Every number is melted down here into the primes it was built from — and no two recipes ever agree.',
  conceptIds: ['factors-multiples', 'prime-composite', 'prime-factorization', 'gcf-lcm'],
};

const ROOT_AND_POWER_RIDGE: Region = {
  id: 'region-root-and-power-ridge',
  name: 'Root and Power Ridge',
  emoji: '🗻',
  flavor: 'Climb by powers, descend by roots — and at the far end, the numbers that need a second dimension.',
  conceptIds: [
    'square-roots',
    'cube-roots',
    'radicals',
    'rational-exponents',
    'scientific-notation',
    'complex-numbers',
    'complex-polar',
  ],
};

/* -------------------------------------------------------------------------- */
/* Fraction Kingdom                                                           */
/* -------------------------------------------------------------------------- */

const SLICE_VILLAGE: Region = {
  id: 'region-slice-village',
  name: 'Slice Village',
  emoji: '🍰',
  flavor: 'Everything here is cut into equal parts, and the trick is remembering what the whole one was.',
  conceptIds: ['fraction-basics', 'fractions-on-number-line', 'whole-number-fractions'],
};

const EQUIVALENCE_MILL: Region = {
  id: 'region-equivalence-mill',
  name: 'Equivalence Mill',
  emoji: '🔁',
  flavor: 'The mill that grinds fractions into other names for themselves, so any two can be compared.',
  conceptIds: ['equivalent-fractions', 'simplify-fractions', 'compare-fractions', 'common-denominators'],
};

const MIXING_KITCHENS: Region = {
  id: 'region-mixing-kitchens',
  name: 'Mixing Kitchens',
  emoji: '🥧',
  flavor: 'Adding and multiplying parts of things, where the two operations follow stubbornly different rules.',
  conceptIds: [
    'add-like-fractions',
    'add-fractions',
    'add-mixed-numbers',
    'mixed-numbers',
    'fraction-times-whole',
    'multiply-fractions',
  ],
};

const DIVIDERS_HOLLOW: Region = {
  id: 'region-dividers-hollow',
  name: "Divider's Hollow",
  emoji: '🔪',
  flavor: 'The valley where dividing makes things bigger, and everyone has to look at it twice.',
  conceptIds: ['fraction-as-division', 'fraction-scaling', 'divide-unit-fractions', 'divide-fractions'],
};

const RATIO_MARKET: Region = {
  id: 'region-ratio-market',
  name: 'Ratio Market',
  emoji: '🏪',
  flavor: 'Three for a coin, sixty per cent off, two parts to one — the market runs entirely on comparison.',
  conceptIds: ['ratios', 'unit-rates', 'percentages', 'proportions', 'percent-applications'],
};

/* -------------------------------------------------------------------------- */
/* Algebra Kingdom                                                            */
/* -------------------------------------------------------------------------- */

const VARIABLE_VILLAGE: Region = {
  id: 'region-variable-village',
  name: 'Variable Village',
  emoji: '🔤',
  flavor: 'Houses with letters instead of numbers on the door, standing in for whoever happens to live there.',
  conceptIds: [
    'variables',
    'numerical-expressions',
    'number-pattern-pairs',
    'algebraic-expressions',
    'linear-expressions',
    'exponents',
    'exponent-rules',
  ],
};

const EQUATION_FOREST: Region = {
  id: 'region-equation-forest',
  name: 'Equation Forest',
  emoji: '🌳',
  flavor: 'Both sides of every path must balance, and the way through is to do the same thing to each.',
  conceptIds: [
    'one-step-equations',
    'multi-step-equations',
    'linear-equation-solutions',
    'equations-with-fractions',
    'literal-equations',
    'proportional-relationships',
    'two-variable-relationships',
  ],
};

const INEQUALITY_MARSH: Region = {
  id: 'region-inequality-marsh',
  name: 'Inequality Marsh',
  emoji: '〰️',
  flavor: 'Ground that is never exactly level. Answers here are whole stretches of the number line, not points.',
  conceptIds: [
    'one-variable-inequalities',
    'inequalities',
    'compound-inequalities',
    'two-variable-inequalities',
    'absolute-value',
    'polynomial-inequalities',
  ],
};

const POLYNOMIAL_PLAINS: Region = {
  id: 'region-polynomial-plains',
  name: 'Polynomial Plains',
  emoji: '🏞️',
  flavor: 'Long open expressions that can be folded into factors — and folded back again to check.',
  conceptIds: [
    'polynomials',
    'factoring',
    'polynomial-division',
    'rational-expressions',
    'rational-equations',
    'radical-equations',
    'recursive-sequences',
    'binomial-theorem',
  ],
};

const QUADRATIC_CASTLE: Region = {
  id: 'region-quadratic-castle',
  name: 'Quadratic Castle',
  emoji: '🏯',
  flavor: 'A fortress with two doors and one formula that always opens both, however ugly the numbers.',
  conceptIds: [
    'quadratics',
    'completing-the-square',
    'quadratic-formula',
    'systems-of-equations',
    'nonlinear-systems',
  ],
};

/* -------------------------------------------------------------------------- */
/* Geometry Kingdom                                                           */
/* -------------------------------------------------------------------------- */

const MEASURERS_VALE: Region = {
  id: 'region-measurers-vale',
  name: "Measurer's Vale",
  emoji: '📏',
  flavor: 'A valley of rope and rulers, where length, time, mass, area and volume are all one question asked five ways.',
  conceptIds: [
    'telling-time',
    'elapsed-time',
    'mass-and-capacity',
    'unit-conversion',
    'perimeter',
    'area-rectangles',
    'area-composite',
    'area-triangles',
    'volume-unit-cubes',
    'volume-composite',
    'volume',
    'surface-area',
    'round-solids',
  ],
};

const SHAPE_HAMLET: Region = {
  id: 'region-shape-hamlet',
  name: 'Shape Hamlet',
  emoji: '🔷',
  flavor: 'Every roof in the hamlet is a different figure, and the residents argue about what counts as which.',
  conceptIds: [
    'shape-attributes',
    'equal-shares',
    'classify-quadrilaterals',
    'symmetry',
    'polygons',
    'quadrilateral-properties',
  ],
};

const PROOF_RIDGE: Region = {
  id: 'region-proof-ridge',
  name: 'Proof Ridge',
  emoji: '📐',
  flavor: 'The high road where nothing is believed until it is argued for, one angle and one triangle at a time.',
  conceptIds: [
    'angle-measure',
    'angles',
    'angle-relationships',
    'parallel-lines',
    'geometric-proof',
    'conditional-statements',
    'triangle-congruence',
    'triangle-properties',
    'similarity',
    'triangle-proportionality',
    'constructions',
    'special-triangles',
    'pythagoras',
  ],
};

const CIRCLE_SANCTUM: Region = {
  id: 'region-circle-sanctum',
  name: 'Circle Sanctum',
  emoji: '⭕',
  flavor: 'One point, one distance, and a surprising number of theorems about what that forces to be true.',
  conceptIds: ['circles', 'circle-theorems', 'chords-secants', 'arc-sector', 'conic-sections'],
};

const COORDINATE_COAST: Region = {
  id: 'region-coordinate-coast',
  name: 'Coordinate Coast',
  emoji: '🧭',
  flavor: 'Where shape meets number: every point has an address, so every move can be written down.',
  conceptIds: [
    'coordinate-first-quadrant',
    'coordinate-plane',
    'coordinate-distance',
    'coordinate-geometry',
    'polar-coordinates',
    'transformations',
    'scale-drawings',
  ],
};

/* -------------------------------------------------------------------------- */
/* Function Kingdom                                                           */
/* -------------------------------------------------------------------------- */

const FUNCTION_FOUNDRY: Region = {
  id: 'region-function-foundry',
  name: 'Function Foundry',
  emoji: '⚙️',
  flavor: 'Machines that take one number and hand back exactly one other — and the graphs that show their shape.',
  conceptIds: [
    'functions-intro',
    'linear-functions',
    'comparing-functions',
    'linear-equation-forms',
    'domain-and-range',
    'function-operations',
    'function-transformations',
    'inverse-functions',
    'piecewise-functions',
    'quadratic-graphs',
    'polynomial-functions',
    'rational-functions',
    'radical-functions',
  ],
};

const LOGARITHM_LIGHTHOUSE: Region = {
  id: 'region-logarithm-lighthouse',
  name: 'Logarithm Lighthouse',
  emoji: '🔦',
  flavor: 'Growth that outruns every straight line, and the one instrument that can read it back down to size.',
  conceptIds: [
    'exponential-functions',
    'exponential-models',
    'exponential-equations',
    'logarithms',
    'natural-logarithm',
  ],
};

const SEQUENCE_SPIRAL: Region = {
  id: 'region-sequence-spiral',
  name: 'Sequence Spiral',
  emoji: '🌀',
  flavor: 'A stair that never quite stops, where you prove the next step by standing on the one below.',
  conceptIds: ['sequences', 'series', 'mathematical-induction'],
};

/* -------------------------------------------------------------------------- */
/* Statistics Kingdom                                                         */
/* -------------------------------------------------------------------------- */

const DATA_OBSERVATORY: Region = {
  id: 'region-data-observatory',
  name: 'Data Observatory',
  emoji: '📊',
  flavor: 'Instruments for looking at a crowd of numbers and asking it a fair question.',
  conceptIds: [
    'scaled-graphs',
    'line-plots',
    'data-displays',
    'mean-median-mode',
    'histograms',
    'data-variability',
    'scatter-plots',
    'two-way-tables',
  ],
};

const CHANCE_FEN: Region = {
  id: 'region-chance-fen',
  name: 'Chance Fen',
  emoji: '🎲',
  flavor: 'Marshland where nothing is certain, but everything can still be counted before it happens.',
  conceptIds: [
    'basic-probability',
    'compound-probability',
    'experimental-probability',
    'probability-distributions',
    'combinatorics-intro',
    'counting-probability',
  ],
};

const SAMPLE_SPACE_SHORE: Region = {
  id: 'region-sample-space-shore',
  name: 'Sample Space Shore',
  emoji: '🏖️',
  flavor: 'Everything that could happen, written down — and what is left of it once you learn something.',
  conceptIds: ['sample-spaces', 'probability-events', 'conditional-probability', 'independence', 'bayes-theorem'],
};

const RANDOM_VARIABLE_WORKS: Region = {
  id: 'region-random-variable-works',
  name: 'Random Variable Works',
  emoji: '🎰',
  flavor: 'Machines whose output is decided by chance, and the handful of shapes that output keeps taking.',
  conceptIds: [
    'discrete-random-variables',
    'expected-value',
    'random-variable-variance',
    'random-variable-sd',
    'continuous-random-variables',
    'binomial-distribution',
    'geometric-distribution',
    'poisson-distribution',
    'normal-random-variable',
    'normal-distribution',
  ],
};

const INFERENCE_COURT: Region = {
  id: 'region-inference-court',
  name: 'Inference Court',
  emoji: '⚖️',
  flavor: 'Where a sample is asked what it can honestly say about the population it came from.',
  conceptIds: [
    'sampling',
    'standard-deviation',
    'sampling-distributions',
    'confidence-intervals',
    'hypothesis-testing',
    'correlation',
    'regression',
    'linear-regression',
  ],
};

/* -------------------------------------------------------------------------- */
/* Linear Algebra Kingdom                                                     */
/* -------------------------------------------------------------------------- */

const VECTOR_HARBOUR: Region = {
  id: 'region-vector-harbour',
  name: 'Vector Harbour',
  emoji: '➡️',
  flavor: 'Arrows with a length and a direction, and the question of everywhere a handful of them can reach.',
  conceptIds: ['vectors', 'dot-product', 'vectors-rn', 'linear-combinations', 'span', 'linear-independence'],
};

const MATRIX_FOUNDRY: Region = {
  id: 'region-matrix-foundry',
  name: 'Matrix Foundry',
  emoji: '🔲',
  flavor: 'Grids of numbers hammered into shape row by row, until what they were hiding is on the surface.',
  conceptIds: ['matrices', 'row-reduction', 'gaussian-elimination', 'matrix-inverse', 'determinants'],
};

const SYSTEM_HALLS: Region = {
  id: 'region-system-halls',
  name: 'System Halls',
  emoji: '🏛️',
  flavor: 'Many equations at once, and the three answers they can give: exactly one, none, or endlessly many.',
  conceptIds: ['linear-systems', 'rank', 'solution-sets'],
};

const VECTOR_SPACE_ARCHIVES: Region = {
  id: 'region-vector-space-archives',
  name: 'Vector Space Archives',
  emoji: '📚',
  flavor: 'The rules an arrow obeyed, kept without the arrow — and the maps between anything that obeys them.',
  conceptIds: [
    'vector-spaces',
    'subspaces',
    'basis',
    'dimension',
    'coordinates',
    'linear-transformations',
    'kernel-range',
    'transformation-matrix',
  ],
};

const EIGEN_SPIRE: Region = {
  id: 'region-eigen-spire',
  name: 'Eigen Spire',
  emoji: '🗼',
  flavor: 'At the top, the directions a transformation cannot turn — only stretch. Everything else follows.',
  conceptIds: ['eigenvalues', 'eigenvectors', 'diagonalization', 'eigen-applications'],
};

/* -------------------------------------------------------------------------- */
/* Trigonometry Peaks                                                         */
/* -------------------------------------------------------------------------- */

const RATIO_BASECAMP: Region = {
  id: 'region-ratio-basecamp',
  name: 'Ratio Basecamp',
  emoji: '⛺',
  flavor: 'Camp at the foot of the mountain, where three sides of a right triangle first get names.',
  conceptIds: ['trig-ratios', 'trig-applications'],
};

const UNIT_CIRCLE_SUMMIT: Region = {
  id: 'region-unit-circle-summit',
  name: 'Unit Circle Summit',
  emoji: '🎡',
  flavor: 'The turning wheel at the top, where an angle stops being a corner and becomes a journey round.',
  conceptIds: ['unit-circle', 'trig-graphs', 'tangent-function'],
};

const IDENTITY_RIDGE: Region = {
  id: 'region-identity-ridge',
  name: 'Identity Ridge',
  emoji: '🏔️',
  flavor: 'A ridge of statements true for every angle at once — and the equations that are true for only a few.',
  conceptIds: [
    'trig-identities',
    'trig-addition-formulas',
    'half-angle-formulas',
    'trig-equations',
    'law-of-sines-cosines',
  ],
};

/* -------------------------------------------------------------------------- */
/* Calculus Kingdom                                                           */
/* -------------------------------------------------------------------------- */

const LIMIT_GATE: Region = {
  id: 'region-limit-gate',
  name: 'Limit Gate',
  emoji: '🚪',
  flavor: 'The gate you approach forever and reach exactly. Everything past it is built on this one idea.',
  conceptIds: ['limits', 'continuity', 'rates-of-change'],
};

const DERIVATIVE_HEIGHTS: Region = {
  id: 'region-derivative-heights',
  name: 'Derivative Heights',
  emoji: '⛰️',
  flavor: 'Steep ground where the only thing that matters is how fast it is changing under your feet.',
  conceptIds: [
    'derivatives',
    'derivative-rules',
    'implicit-differentiation',
    'derivative-applications',
    'optimization',
    'motion-derivatives',
  ],
};

const INTEGRAL_DEPTHS: Region = {
  id: 'region-integral-depths',
  name: 'Integral Depths',
  emoji: '🕳️',
  flavor: 'Down where the infinitely small is added up, and the answer comes back a finite area, length or mass.',
  conceptIds: [
    'antiderivatives',
    'integrals',
    'fundamental-theorem',
    'u-substitution',
    'integration-by-parts',
    'trigonometric-integrals',
    'trigonometric-substitution',
    'partial-fractions',
    'improper-integrals',
    'arc-length',
    'surface-of-revolution',
    'work-integrals',
    'center-of-mass',
    'parametric-curves',
    'polar-calculus',
  ],
};

const INFINITY_REALM: Region = {
  id: 'region-infinity-realm',
  name: 'Infinity Realm',
  emoji: '🌌',
  flavor: 'Sums with no last term, some of which settle on a number anyway — and functions written as one.',
  conceptIds: [
    'sequence-limits',
    'series-convergence',
    'integral-test',
    'comparison-tests',
    'ratio-test',
    'alternating-series',
    'power-series',
    'taylor-series',
  ],
};

/* -------------------------------------------------------------------------- */
/* The chain                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The share of a requirement's concepts a student must already know.
 *
 * Four in five, not five in five. A single concept a student happens not to
 * have met — often one their school skipped — should not hold a whole kingdom
 * shut, and demanding everything turns a story beat into a chore
 * (`docs/rpg-implementation-plan.md` §0.6).
 */
const CHAIN_SHARE = 0.8;

/** An unlock clause naming every concept in the given regions. */
const knownFrom = (...regions: Region[]): UnlockRequirement['known'][number] => ({
  conceptIds: regions.flatMap((region) => region.conceptIds),
  share: CHAIN_SHARE,
});

/** Always open. Starter Village, and the shape a new student's world starts in. */
const ALWAYS_OPEN: UnlockRequirement = { known: [], clearedBossIds: [] };

const hueOf = (strandId: StrandId): number => {
  const strand = STRANDS.find((candidate) => candidate.id === strandId);
  if (!strand) throw new Error(`Unknown strand: ${strandId}`);
  return strand.hue;
};

export const KINGDOMS: Kingdom[] = [
  {
    id: 'kingdom-starter-village',
    name: 'Starter Village',
    emoji: '🌱',
    tagline: 'Where every adventurer begins — counting, the four operations, and the confidence to leave.',
    strandIds: ['early-math', 'arithmetic'],
    order: 0,
    hue: hueOf('early-math'),
    bossId: 'boss-starter-village',
    regions: [COUNTING_MEADOW, SUM_COTTAGE, TIMES_TABLE_GROVE, SHARING_BARN, TWO_STEP_TRAIL],
    unlock: ALWAYS_OPEN,
  },
  {
    id: 'kingdom-number',
    name: 'Number Kingdom',
    emoji: '🔢',
    tagline: 'The kingdom the whole number system is built in: place value, decimals, negatives and primes.',
    strandIds: ['numbers', 'decimals', 'number-theory', 'arithmetic'],
    order: 1,
    hue: hueOf('numbers'),
    bossId: 'boss-number-kingdom',
    regions: [PLACE_VALUE_PLAINS, DECIMAL_DOCKS, INTEGER_CAVERNS, PRIME_FOUNDRY, ROOT_AND_POWER_RIDGE],
    unlock: {
      known: [knownFrom(COUNTING_MEADOW, SUM_COTTAGE, TIMES_TABLE_GROVE, SHARING_BARN)],
      clearedBossIds: [],
    },
  },
  {
    id: 'kingdom-fraction',
    name: 'Fraction Kingdom',
    emoji: '🍰',
    tagline: 'Parts of a whole, and everything that follows from comparing them: ratios, rates and percent.',
    strandIds: ['fractions', 'numbers'],
    order: 2,
    hue: hueOf('fractions'),
    bossId: 'boss-fraction-kingdom',
    regions: [SLICE_VILLAGE, EQUIVALENCE_MILL, MIXING_KITCHENS, DIVIDERS_HOLLOW, RATIO_MARKET],
    unlock: {
      known: [knownFrom(PLACE_VALUE_PLAINS, PRIME_FOUNDRY)],
      clearedBossIds: [],
    },
  },
  {
    id: 'kingdom-algebra',
    name: 'Algebra Kingdom',
    emoji: '⚔️',
    tagline: 'Letters standing in for numbers, and the rules that let you find out which numbers they were.',
    strandIds: ['algebra'],
    order: 3,
    hue: hueOf('algebra'),
    bossId: 'boss-algebra-kingdom',
    regions: [VARIABLE_VILLAGE, EQUATION_FOREST, INEQUALITY_MARSH, POLYNOMIAL_PLAINS, QUADRATIC_CASTLE],
    unlock: {
      known: [knownFrom(EQUIVALENCE_MILL, MIXING_KITCHENS, RATIO_MARKET)],
      clearedBossIds: [],
    },
  },
  {
    id: 'kingdom-geometry',
    name: 'Geometry Kingdom',
    emoji: '🏰',
    tagline: 'Shape, space and measurement — and the first place on the map where you have to prove it.',
    strandIds: ['geometry', 'measurement', 'discrete'],
    order: 4,
    hue: hueOf('geometry'),
    bossId: 'boss-geometry-kingdom',
    regions: [MEASURERS_VALE, SHAPE_HAMLET, PROOF_RIDGE, CIRCLE_SANCTUM, COORDINATE_COAST],
    unlock: {
      known: [knownFrom(VARIABLE_VILLAGE, EQUATION_FOREST)],
      clearedBossIds: [],
    },
  },
  {
    id: 'kingdom-linear-algebra',
    name: 'Linear Algebra Kingdom',
    emoji: '🧮',
    tagline: 'The other road out of school algebra: arrows, grids of numbers, and the geometry hiding in both.',
    strandIds: ['linear-algebra'],
    order: 5,
    hue: hueOf('linear-algebra'),
    bossId: 'boss-linear-algebra-kingdom',
    regions: [VECTOR_HARBOUR, MATRIX_FOUNDRY, SYSTEM_HALLS, VECTOR_SPACE_ARCHIVES, EIGEN_SPIRE],
    /*
     * Vectors and systems, and nothing else. Nothing in linear algebra depends
     * on a limit, a derivative or an integral — `content/src/linear-algebra.test.ts`
     * walks the prerequisites to prove it — so sealing this kingdom behind
     * Calculus would contradict the graph in order to tidy up the map. A student
     * heading for engineering takes both roads, in either order.
     */
    unlock: {
      known: [knownFrom(EQUATION_FOREST, QUADRATIC_CASTLE), knownFrom(COORDINATE_COAST)],
      clearedBossIds: [],
    },
  },
  {
    id: 'kingdom-function',
    name: 'Function Kingdom',
    emoji: '⚙️',
    tagline: 'Machines that turn one number into another, and every shape the graph of one can take.',
    strandIds: ['functions', 'discrete'],
    order: 6,
    hue: hueOf('functions'),
    bossId: 'boss-function-kingdom',
    regions: [FUNCTION_FOUNDRY, LOGARITHM_LIGHTHOUSE, SEQUENCE_SPIRAL],
    unlock: {
      known: [knownFrom(EQUATION_FOREST, QUADRATIC_CASTLE), knownFrom(COORDINATE_COAST)],
      clearedBossIds: [],
    },
  },
  {
    id: 'kingdom-statistics',
    name: 'Statistics Kingdom',
    emoji: '📊',
    tagline: 'The mathematics of what you did not measure: chance, the shapes it takes, and what a sample proves.',
    strandIds: ['statistics', 'probability', 'discrete'],
    order: 7,
    hue: hueOf('statistics'),
    bossId: 'boss-statistics-kingdom',
    regions: [DATA_OBSERVATORY, CHANCE_FEN, SAMPLE_SPACE_SHORE, RANDOM_VARIABLE_WORKS, INFERENCE_COURT],
    /*
     * Ratios first, because every probability is one, and functions second,
     * because a distribution is a function before it is anything else. Not
     * calculus: the continuous work here is read off tables and named shapes,
     * and requiring an integral would seal a whole kingdom of school statistics
     * behind a course its students have not reached.
     */
    unlock: {
      known: [knownFrom(RATIO_MARKET), knownFrom(FUNCTION_FOUNDRY)],
      clearedBossIds: [],
    },
  },
  {
    id: 'kingdom-trigonometry',
    name: 'Trigonometry Peaks',
    emoji: '🏔️',
    tagline: 'The mountains where a triangle becomes a circle, and a circle becomes a wave.',
    strandIds: ['trigonometry'],
    order: 8,
    hue: hueOf('trigonometry'),
    bossId: 'boss-trigonometry-kingdom',
    regions: [RATIO_BASECAMP, UNIT_CIRCLE_SUMMIT, IDENTITY_RIDGE],
    unlock: {
      known: [knownFrom(PROOF_RIDGE, CIRCLE_SANCTUM), knownFrom(FUNCTION_FOUNDRY)],
      clearedBossIds: [],
    },
  },
  {
    id: 'kingdom-calculus',
    name: 'Calculus Kingdom',
    emoji: '🌌',
    tagline: 'Change and accumulation, and the sums with no last term that both of them lean on.',
    strandIds: ['calculus'],
    order: 9,
    hue: hueOf('calculus'),
    bossId: 'boss-calculus-kingdom',
    regions: [LIMIT_GATE, DERIVATIVE_HEIGHTS, INTEGRAL_DEPTHS, INFINITY_REALM],
    unlock: {
      known: [knownFrom(FUNCTION_FOUNDRY, LOGARITHM_LIGHTHOUSE), knownFrom(UNIT_CIRCLE_SUMMIT, IDENTITY_RIDGE)],
      clearedBossIds: [],
    },
  },
];
